/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Address, AddressPurpose, CountryRegionInfo, StateProvinceInfo } from '@msdyn365-commerce/retail-proxy';
import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observable, reaction, set } from 'mobx';
import * as React from 'react';

import { AddressCommon } from '@msdyn365-commerce-modules/address';
import { AddressFormat } from '@msdyn365-commerce-modules/address';
import { AddressItemDisplayType, AddressItemType, AddressValidationRuleType, IAddressItem } from '@msdyn365-commerce-modules/address';
import { AddressMetaData } from '@msdyn365-commerce-modules/address';
import { AddressOperation, AddressType, IAddressResource, IAddressResponse } from '@msdyn365-commerce-modules/address';
import { AddressAddUpdate, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address';
import { IBusinessAccountAddressData } from './business-account-address.data';
import { IBusinessAccountAddressProps } from './business-account-address.props.autogenerated';

/**
 * Interface for IBusinessAccountAddressExtendedProps.
 */
export interface IBusinessAccountAddressExtendedProps extends IBusinessAccountAddressProps<IBusinessAccountAddressData> {
    hasError: boolean;
    isRequired: boolean;
    resetAddress: boolean;
    updateForm(errors: boolean, currentAddress?: Address): void;
}

/**
 * Interface for IBusinessAccountAddressState.
 */
export interface IBusinessAccountAddressState {
    countryId?: string;
    states?: StateProvinceInfo[];
}

/**
 * Interface for IBusinessAccountAddressViewProps.
 */
export interface IBusinessAccountAddressViewProps {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    addressListSelectedAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    customerAddresses: Address[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    BusinessAccountAddress: IModuleProps;
    isUpdating: boolean;
    hasError: boolean;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
}

/**
 *
 * Address component.
 * @extends {React.Component<IBusinessAccountAddressProps<IBusinessAccountAddressData>, IBusinessAccountAddressState>}
 */
class BusinessAccountAddress extends React.Component<IBusinessAccountAddressExtendedProps, IBusinessAccountAddressState> {
    @observable private addUpdateAddress: Address;

    @observable private countryRegionId: string = 'USA';

    @observable private stateProvinceInfo?: StateProvinceInfo[];

    @observable private readonly validationError: object;

    private readonly addressActionResponse?: IAddressResponse;

    private readonly isUpdating?: boolean;

    private readonly addressCommon: AddressCommon;

    private addressFormat: AddressFormat;

    private countryRegions: CountryRegionInfo[] = [];

    private addressPurposes: AddressPurpose[] = [];

    private readonly resources: IAddressResource;

    private readonly defaultAddressType: number = 8; // Default to Business

    private readonly shouldAutoFocus?: boolean = false;

    private readonly excludedList: AddressItemType[] = [AddressItemType.Name, AddressItemType.AddressTypeValue, AddressItemType.IsPrimary]; // Default to hiding Name and AddressType inputs

    private readonly telemetryContent: ITelemetryContent;

    public constructor(props: IBusinessAccountAddressExtendedProps) {
        super(props);

        const { context, data, resources, telemetry } = this.props;
        this.addUpdateAddress = {};
        this.resources = resources;
        this.countryRegions = data.countryRegions.result || [];
        this.addressPurposes = data.addressPurposes.result || [];
        this.stateProvinceInfo = data.countryStates.result || [];
        this.addressFormat = new AddressFormat(
            this.countryRegions,
            new AddressMetaData({ ...resources }, this.excludedList, props.isRequired ? undefined : []),
            this.addressPurposes
        );
        this.addressCommon = new AddressCommon(context, resources, telemetry);
        this.validationError = {};
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        // Initializing data props
        this._dataInitialize(this.props);

        this._setDefaultCountryRegionId();
    }

    public shouldComponentUpdate(nextProps: IBusinessAccountAddressExtendedProps, nextState: IBusinessAccountAddressState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const { renderView, config, hasError, resetAddress } = this.props;

        // Business-sign-up will pass this flag when cancel button is clicked
        if (resetAddress) {
            this._resetAddressFields();
        }

        const viewProps = {
            ...this.props,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            hasError,
            className: config.className,
            BusinessAccountAddress: {
                moduleProps: this.props,
                className: classnames('ms-business-account-address', config.className)
            },
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress()
        };

        return renderView(viewProps) as React.ReactElement;
    }

    /**
     * Reset Address.
     */
    public _resetAddressFields = () => {
        const addressFormat = this.addressFormat.getAddressFormat(this.countryRegionId);

        addressFormat.map(addressFormatItem => {
            switch (addressFormatItem.displayType) {
                case AddressItemDisplayType.Input:
                    this.addUpdateAddress[addressFormatItem.name] = '';
                    break;
                case AddressItemDisplayType.Checkbox:
                    this.addUpdateAddress[addressFormatItem.name] = false;
                    break;
                default:
                    switch (addressFormatItem.type) {
                        case AddressItemType.ThreeLetterISORegionName:
                            this.addUpdateAddress[addressFormatItem.name] = this.countryRegionId;
                            break;
                        case AddressItemType.AddressTypeValue:
                            this.addUpdateAddress[addressFormatItem.name] = this.defaultAddressType;
                            break;
                        case AddressItemType.State:
                            this.addUpdateAddress[addressFormatItem.name] = '';
                            break;
                        default:
                            this.addUpdateAddress[addressFormatItem.name] = undefined;
                    }
            }
        });
    };

    /**
     * Method data initialization.
     * @param props -The account management props.
     */
    private readonly _dataInitialize = (props: IBusinessAccountAddressExtendedProps): void => {
        const { data } = props;

        reaction(
            () => data.countryRegions.result,
            () => {
                this.countryRegions = data.countryRegions.result ?? [];
            }
        );

        reaction(
            () => data.addressPurposes.result,
            () => {
                this.addressPurposes = data.addressPurposes.result ?? [];
            }
        );

        reaction(
            () => data.countryStates.result,
            () => {
                this.stateProvinceInfo = data.countryStates.result ?? [];
            }
        );
    };

    // Set validationError and hasError to hide error messages since in the B2B
    // request form, no errors would visually show since user wont be able to submit
    // until form is filled and correct
    /**
     * Render Add Or Update Address Function.
     * @returns - AddressAddUpdate.
     */
    private readonly _renderAddOrUpdateAddress = (): IAddressAddUpdateProps => {
        return AddressAddUpdate({
            isUpdating: this.isUpdating,
            resources: this.resources,
            addressType: AddressType.Company,
            addressFormat: this.addressFormat.getAddressFormat(this.countryRegionId),
            defaultCountryRegionId: this.countryRegionId,
            defaultAddressType: this.defaultAddressType,
            selectedAddress: this.addUpdateAddress,
            shouldAutoFocus: this.shouldAutoFocus,
            validationError: this.validationError,
            hasError: this.props.hasError,
            addressActionResponse: this.addressActionResponse,
            telemetryContent: this.telemetryContent,
            dropdownDisplayData: this.addressFormat.getPrefilledAddressDropdownData(
                this.resources.addressStateDefaultSelectionText,
                this.stateProvinceInfo
            ),
            onInputChange: this._onAddressAddUpdateInputChange,
            onDropdownChange: this._onAddressAddUpdateDropdownChange
        });
    };

    /**
     * On Address Add Update Input Change Function.
     * @param event - Input event change function.
     */
    private readonly _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this._updateAddress(event.target.name, event.target.value);
    };

    /**
     * On Address Add Update Dropdown Change Function.
     * @param event - Change dropdown event.
     */
    private readonly _onAddressAddUpdateDropdownChange = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            await this._updateCountryRegionId(event.target.value);
        } else {
            this._updateAddress(event.target.name, event.target.value);
        }
    };

    /**
     * Update Address Function.
     * @param name - String.
     * @param value - String.
     */
    private readonly _updateAddress = (name: string, value: string | number) => {
        const { updateForm, isRequired, resources } = this.props;
        let cleanValue = value;
        if (typeof value === 'string') {
            cleanValue = (value || '').replace(new RegExp('[<>]', 'gi'), '');
        }
        set(this.addUpdateAddress, { [name]: cleanValue });

        if (this._isEmpty()) {
            if (isRequired) {
                updateForm(true, undefined);
            } else {
                // Reset addressFormat as if address was NOT a required field
                this.addressFormat = new AddressFormat(
                    this.countryRegions,
                    new AddressMetaData({ ...resources }, this.excludedList, []),
                    this.addressPurposes
                );
                updateForm(false, this.addUpdateAddress);
            }
        } else {
            // Get new addressFormat as if address was a required field
            this.addressFormat = new AddressFormat(
                this.countryRegions,
                new AddressMetaData({ ...resources }, this.excludedList, undefined),
                this.addressPurposes
            );

            // Verify with new rules
            if (this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId)) {
                updateForm(false, this.addUpdateAddress);
            } else {
                updateForm(true, undefined);
            }
        }
    };

    /**
     * Is empty Function.
     * @returns - Boolean.
     */
    // Walk through array and check if valued is defined or value is not an empty string
    private readonly _isEmpty = () => {
        const addressFormat = this.addressFormat.getAddressFormat(this.countryRegionId);
        for (const addressFormatItem of addressFormat) {
            if (addressFormatItem.type !== AddressItemType.ThreeLetterISORegionName || this._isInputRequired(addressFormatItem)) {
                const addressItemValue = this.addUpdateAddress[addressFormatItem.name];

                // Check each input field, if value is defined and not an empty string --> address is not empty
                if (addressItemValue || (addressItemValue && addressItemValue !== '')) {
                    return false;
                }
            }
        }

        return true;
    };

    /**
     * Is Input Required Function.
     * @param addressFormatItem - IAddressItem.
     * @returns - Boolean.
     */
    private readonly _isInputRequired = (addressFormatItem: IAddressItem): boolean => {
        if (
            ObjectExtensions.isNullOrUndefined(addressFormatItem) ||
            !addressFormatItem.validationRules ||
            addressFormatItem.validationRules.length === 0
        ) {
            return false;
        }

        for (const validationRule of addressFormatItem.validationRules) {
            if (validationRule.type === AddressValidationRuleType.Required) {
                return false;
            }
        }

        return true;
    };

    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const market = request.channel?.ChannelCountryRegionISOCode;

        void this._updateCountryRegionId(this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market));

        // Set default AddressType
        set(this.addUpdateAddress, { AddressTypeValue: this.defaultAddressType });
    }

    /**
     * Update Country Region Id Function.
     * @param value - String.
     */
    private readonly _updateCountryRegionId = async (value: string) => {
        this.countryRegionId = value;

        await this.addressCommon.getStateProvinces(this.countryRegionId).then((result: StateProvinceInfo[]) => {
            const stateInfo = result.some(state => state.StateId === this.addUpdateAddress.State);

            // Reset state if selected state not found in the list.
            if (!stateInfo) {
                this._updateAddress('State', '');
            }

            this.stateProvinceInfo = result;
        });

        this._updateAddress('ThreeLetterISORegionName', this.countryRegionId);
    };
}

export default BusinessAccountAddress;
